import { Dropdown } from "./Dropdown.js";

/**
 * Class to implement the dropdown manager
 * as a singleton, which can be instantiated by
 * any other extension as a dependency
 */
export class DropdownManager {
    private static manager?: DropdownManager;
    private instances: Dropdown[] = [];

    /**
     * The private constructor
     * prevents direct instantiation
     */
    private constructor() {
        this.dropdowns.forEach((dropdown) => {
            const menu = Array.from(
                dropdown.querySelectorAll<HTMLElement>("ul"),
            ).find((element) => element.closest("[dropdown]") === dropdown);
            const toggler = Array.from(
                dropdown.querySelectorAll<HTMLElement>("[aria-controls]"),
            ).find(
                (element) =>
                    element.closest("[dropdown]") === dropdown &&
                    element.getAttribute("aria-controls") === menu?.id,
            );
            if (!menu?.id || !toggler) {
                return;
            }
            this.instances.push(new Dropdown(menu, toggler));
        });
    }

    /**
     * Method to instantiate all
     * dropdowns on the current page
     *
     * @param {boolean} [reset=false]
     * @returns {DropdownManager}
     */
    public static use(reset: boolean = false): DropdownManager {
        if (reset) {
            DropdownManager.manager?.destroy();
        }
        if (!DropdownManager.manager) {
            DropdownManager.manager = new DropdownManager();
        }
        return DropdownManager.manager;
    }

    public destroy(): void {
        this.instances.forEach((instance) => instance.destroy());
        this.instances = [];
        if (DropdownManager.manager === this) {
            DropdownManager.manager = undefined;
        }
    }

    /**
     * Selects the dropdowns
     *
     * @returns {NodeListOf<HTMLElement>}
     */
    protected get dropdowns(): NodeListOf<HTMLElement> {
        return document.querySelectorAll(`[dropdown]`);
    }

    /**
     * Returns the number of instantiated dropdowns
     *
     * @returns {number}
     */
    public get length(): number {
        return this.instances.length;
    }
}
